import { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button, CircularProgress, TextField, InputAdornment } from "@mui/material";
import { ArrowUpward, ArrowDownward, ImportExport, Search } from "@mui/icons-material";
import PopAddSpace from "../../components/Space/PopAddSpace";
import Swal from "sweetalert2";


const Spaces = () => {

    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [popAddSpace, setPopAddSpace] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSpaces = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/space`, { method: 'GET' });
                const json = await res.json();
                setSpaces(json.spaces);
            } catch (error) {
                console.error("Failed to fetch spaces:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSpaces();
    }, []);


    const onAddSpace = (e, space_id) => {
        setPopAddSpace(false);
        console.log(space_id);
        navigate(`/space/${space_id}`);

    }

    const onDeleteSpace = async (space_id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/space/${space_id}`, {
                    method: 'DELETE'
                });

                if (!response.ok) {
                    throw new Error('Failed to delete space');
                }

                setSpaces(prevSpaces => prevSpaces.filter(space => space.space_id !== space_id));
                Swal.fire('Deleted!', 'The space has been deleted.', 'success');
            } catch (error) {
                console.error("Deletion failed:", error);
                Swal.fire('Error!', 'Could not delete the space.', 'error');
            }
        }
    }


    const onOpenAddSpacePop = (e) => {
        setPopAddSpace(true);
    }

    const onCloseAddSpacePop = (e) => {
        setPopAddSpace(false);
    }

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key) {
            if (sortConfig.direction === 'ascending') {
                direction = 'descending';
            } else if (sortConfig.direction === 'descending') {
                // Third state: reset to null (default)
                setSortConfig({ key: null, direction: 'ascending' });
                return;
            }
        }
        setSortConfig({ key, direction });
    };

    const sortedSpaces = useMemo(() => {
        let filteredSpaces = spaces.filter(space =>
            space.space_name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        let sortableSpaces = [...filteredSpaces];
        if (sortConfig.key !== null) {
            sortableSpaces.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableSpaces;
    }, [spaces, sortConfig, searchQuery]);

    const getSortIcon = (columnName) => {
        if (sortConfig.key === columnName) {
            return sortConfig.direction === 'ascending' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
        }
        return <ImportExport fontSize="small" style={{ opacity: 0.3 }} />; // Visual hint for unsorted state
    };

    return (
        <>
            <div className="content-header pb-6 flex justify-between">
                <div className="content-header-label">Spaces</div>
                <div className="content-header-actions flex gap-4">
                    <TextField
                        size="small"
                        placeholder="Search spaces..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button onClick={onOpenAddSpacePop}>Add New Space</Button>
                </div>
            </div>
            <div className="content-body">
                {loading ? (
                    <CircularProgress />
                ) : (
                    <table className="table-auto border w-full">
                        <thead>
                            <tr>
                                <th className="font-bold p-2 border-b text-left w-2 cursor-pointer" onClick={() => handleSort('space_id')}>
                                    <div className="flex items-center">
                                        No {getSortIcon('space_id')}
                                    </div>
                                </th>
                                <th className="font-bold p-2 border-b text-left cursor-pointer" onClick={() => handleSort('space_name')}>
                                    <div className="flex items-center">
                                        Space Name {getSortIcon('space_name')}
                                    </div>
                                </th>
                                <th className="font-bold p-2 border-b text-left w-[200px] cursor-pointer" onClick={() => handleSort('space_beacon_id')}>
                                    <div className="flex items-center">
                                        Beacon ID {getSortIcon('space_beacon_id')}
                                    </div>
                                </th>
                                <th className="font-bold py-2 px-4 border-b text-left w-[200px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody>

                            {sortedSpaces.map((space, i) => <tr key={space.space_id}>
                                <td className="p-2 border-b text-left">{i + 1}</td>
                                <td className="p-2 border-b text-left">{space.space_name}</td>
                                <td className="p-2 border-b text-left">{space.space_beacon_id}</td>
                                <td className="py-2 px-4 border-b text-left"> <Link to={`/space/${space.space_id}`}>Edit</Link> / <Button onClick={(e) => onDeleteSpace(space.space_id)}>Delete</Button> </td>
                            </tr>)}

                        </tbody>
                    </table>
                )}
            </div>
            {popAddSpace && <PopAddSpace onAddSpace={onAddSpace} onCloseAddSpacePop={onCloseAddSpacePop} />}

        </>
    )
}

export default Spaces;